import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../utils/api';
import { BUILDINGS } from '../utils/config';
import HistoryPicker from '../components/HistoryPicker';

// 修复中文输入法的Input组件
function CnInput({ value, onChange, placeholder, style, multiline, rows }) {
  const ref = useRef(null);
  const composing = useRef(false);
  const [local, setLocal] = useState(value || '');

  useEffect(() => { setLocal(value || ''); }, [value]);

  const handleChange = (e) => {
    setLocal(e.target.value);
    if (!composing.current) onChange(e.target.value);
  };
  const handleCompositionStart = () => { composing.current = true; };
  const handleCompositionEnd = (e) => {
    composing.current = false;
    setLocal(e.target.value);
    onChange(e.target.value);
  };

  const props = {
    ref, value: local, placeholder, style,
    onChange: handleChange,
    onCompositionStart: handleCompositionStart,
    onCompositionEnd: handleCompositionEnd,
  };
  return multiline ? <textarea {...props} rows={rows || 3} /> : <input {...props} />;
}

function F({ label, children, required }) {
  return (
    <div style={styles.field}>
      <label style={styles.label}>{required && <span style={{color:'#ff4d4f'}}>*</span>}{label}</label>
      {children}
    </div>
  );
}

export default function AddTask({ user }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    title: '', source: '自查', location_type: '公共区域',
    building: '', unit: '', floor: '', room: '',
    public_area: '', department: '', assign_type: 'department',
    assign_user_id: '', priority: '中'
  });
  const [images, setImages] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    if (user.role === 'owner') setForm(f => ({ ...f, source: '业主反应' }));
    API.get('/users').then(r => {
      setUsers(r.data.filter(u => u.is_active && !['owner','committee'].includes(u.role)));
    }).catch(() => {});
  }, [user.role]);

  const buildingList = Object.keys(BUILDINGS);
  const unitList = form.building ? Object.keys(BUILDINGS[form.building]) : [];
  const floorList = form.building && form.unit ? BUILDINGS[form.building][form.unit]?.floors : [];
  const roomList = form.building && form.unit ? BUILDINGS[form.building][form.unit]?.rooms : [];

  const handleImage = (e) => {
    const files = Array.from(e.target.files);
    setImages(prev => [...prev, ...files].slice(0, 5));
    const urls = files.map(f => URL.createObjectURL(f));
    setPreviews(prev => [...prev, ...urls].slice(0, 5));
  };

  const removeImage = (idx) => {
    setImages(prev => prev.filter((_, i) => i !== idx));
    setPreviews(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    if (!form.title) { alert('请填写事项描述'); return; }
    if (form.location_type === '住宅' && (!form.building || !form.unit || !form.floor || !form.room)) {
      alert('请选择完整房号'); return;
    }
    if (form.location_type === '公共区域' && !form.public_area) {
      alert('请选择地点'); return;
    }
    setLoading(true);
    try {
      const data = new FormData();
      Object.keys(form).forEach(k => data.append(k, form[k]));
      images.forEach(img => data.append('images', img));
      await API.post('/tasks', data, { headers: { 'Content-Type': 'multipart/form-data' } });
      alert('提交成功');
      navigate('/');
    } catch (err) {
      alert(err.response?.data?.message || '提交失败');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <button style={styles.back} onClick={() => navigate(-1)}>← 返回</button>
        <h2 style={styles.headerTitle}>新增事项</h2>
        <div style={{width:60}} />
      </div>

      <div style={styles.form}>
        <F label="事项描述" required>
          <CnInput multiline rows={3} style={styles.textarea}
            placeholder="请详细描述问题..."
            value={form.title}
            onChange={v => setForm({...form, title: v})} />
        </F>

        <F label="来源" required>
          <div style={styles.radioGroup}>
            {['自查','业主反应','其他'].map(s => (
              <button key={s}
                style={{...styles.radioBtn, ...(form.source===s ? styles.radioBtnActive : {})}}
                onClick={() => setForm({...form, source: s})}>{s}</button>
            ))}
          </div>
        </F>

        <F label="责任分配">
          <div style={styles.radioGroup}>
            {[['department','分派到部门'],['user','分派到个人']].map(([v,l]) => (
              <button key={v}
                style={{...styles.radioBtn, ...(form.assign_type===v ? styles.radioBtnActive : {})}}
                onClick={() => setForm({...form, assign_type:v, department:'', assign_user_id:''})}>
                {l}
              </button>
            ))}
          </div>
          {form.assign_type === 'department' && (
            <div style={{...styles.radioGroup, marginTop:'8px'}}>
              {['秩序部','工程部','保洁部','管家部','绿化部'].map(d => (
                <button key={d}
                  style={{...styles.radioBtn, ...(form.department===d ? styles.radioBtnActive : {})}}
                  onClick={() => setForm({...form, department: d})}>{d}</button>
              ))}
            </div>
          )}
          {form.assign_type === 'user' && (
            <select style={{...styles.select, marginTop:'8px'}}
              value={form.assign_user_id}
              onChange={e => setForm({...form, assign_user_id: e.target.value})}>
              <option value="">请选择负责人</option>
              {users.map(u => (
                <option key={u.id} value={u.id}>{u.real_name}</option>
              ))}
            </select>
          )}
        </F>

        <F label="优先等级" required>
          <div style={styles.radioGroup}>
            {[['紧急','#ff4d4f'],['高','#ff7a45'],['中','#1890ff'],['低','#52c41a']].map(([p,c]) => (
              <button key={p}
                style={{...styles.radioBtn, ...(form.priority===p ? {background:c, color:'#fff', borderColor:c} : {})}}
                onClick={() => setForm({...form, priority: p})}>{p}</button>
            ))}
          </div>
        </F>

        <F label="事项地点" required>
          <div style={styles.radioGroup}>
            {['住宅','公共区域'].map(t => (
              <button key={t}
                style={{...styles.radioBtn, ...(form.location_type===t ? styles.radioBtnActive : {})}}
                onClick={() => setForm({...form, location_type:t, building:'', unit:'', floor:'', room:'', public_area:''})}>
                {t}
              </button>
            ))}
          </div>
        </F>

        {form.location_type === '住宅' && (
          <>
            <F label="选择幢" required>
              <div style={styles.radioGroup}>
                {buildingList.map(b => (
                  <button key={b}
                    style={{...styles.radioBtn, ...(form.building===b ? styles.radioBtnActive : {})}}
                    onClick={() => setForm({...form, building:b, unit:'', floor:'', room:''})}>
                    {b}
                  </button>
                ))}
              </div>
            </F>
            {form.building && (
              <F label="选择单元" required>
                <div style={styles.radioGroup}>
                  {unitList.map(u => (
                    <button key={u}
                      style={{...styles.radioBtn, ...(form.unit===u ? styles.radioBtnActive : {})}}
                      onClick={() => setForm({...form, unit:u, floor:'', room:''})}>
                      {u}
                    </button>
                  ))}
                </div>
              </F>
            )}
            {form.unit && (
              <>
                <F label="选择楼层" required>
                  <select style={styles.select} value={form.floor}
                    onChange={e => setForm({...form, floor:e.target.value, room:''})}>
                    <option value="">请选择楼层</option>
                    {floorList.map(f => <option key={f} value={f}>{f}层</option>)}
                  </select>
                </F>
                {form.floor && (
                  <F label="选择位置" required>
                    <div style={styles.radioGroup}>
                      <button
                        style={{...styles.radioBtn, ...(form.room==='电梯口' ? styles.radioBtnActive : {})}}
                        onClick={() => setForm({...form, room:'电梯口'})}>
                        🛗 电梯口
                      </button>
                      {roomList.map(r => (
                        <button key={r}
                          style={{...styles.radioBtn, ...(form.room===r ? styles.radioBtnActive : {})}}
                          onClick={() => setForm({...form, room:r})}>
                          {r}室
                        </button>
                      ))}
                    </div>
                  </F>
                )}
              </>
            )}
          </>
        )}

        {form.location_type === '公共区域' && (
          <F label="选择地点" required>
            <HistoryPicker type="task_location" value={form.public_area}
              onChange={v => setForm({...form, public_area:v})} placeholder="输入地点，如：地面、地下室" />
          </F>
        )}

        <F label="拍照上传（最多5张）">
          <div style={styles.imageArea}>
            {previews.map((url, idx) => (
              <div key={idx} style={styles.imgWrap}>
                <img src={url} style={styles.img} alt="" />
                <button style={styles.imgDel} onClick={() => removeImage(idx)}>×</button>
              </div>
            ))}
            {previews.length < 5 && (
              <label style={styles.uploadBtn}>
                <span style={{fontSize:'28px', color:'#999'}}>📷</span>
                <span style={{fontSize:'12px', color:'#999'}}>添加图片</span>
                <input type="file" accept="image/*" multiple style={{display:'none'}} onChange={handleImage} />
              </label>
            )}
          </div>
        </F>

        <button
          style={{...styles.submitBtn, opacity: loading ? 0.7 : 1}}
          onClick={handleSubmit} disabled={loading}>
          {loading ? '提交中...' : '提 交'}
        </button>
      </div>
    </div>
  );
}

const styles = {
  container: { minHeight: '100vh', background: '#f5f7fa', paddingBottom: '30px' },
  header: {
    background: 'linear-gradient(90deg, #1a4a8a, #2d7dd2)',
    padding: '16px', display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', position: 'sticky', top: 0, zIndex: 100
  },
  back: { background: 'transparent', border: 'none', color: '#fff', fontSize: '16px', cursor: 'pointer' },
  headerTitle: { color: '#fff', margin: 0, fontSize: '18px', fontWeight: 700 },
  form: { padding: '16px' },
  field: { marginBottom: '16px' },
  label: { display: 'block', fontWeight: 600, color: '#333', marginBottom: '8px', fontSize: '15px' },
  textarea: { width: '100%', padding: '10px 12px', border: '1.5px solid #e0e0e0', borderRadius: '8px', fontSize: '15px', resize: 'vertical', boxSizing: 'border-box' },
  select: { width: '100%', padding: '10px 12px', border: '1.5px solid #e0e0e0', borderRadius: '8px', fontSize: '15px', background: '#fff' },
  radioGroup: { display: 'flex', flexWrap: 'wrap', gap: '8px' },
  radioBtn: { padding: '8px 16px', border: '1.5px solid #e0e0e0', borderRadius: '20px', background: '#fff', fontSize: '14px', cursor: 'pointer', color: '#333' },
  radioBtnActive: { background: '#1a4a8a', color: '#fff', borderColor: '#1a4a8a' },
  imageArea: { display: 'flex', flexWrap: 'wrap', gap: '10px' },
  imgWrap: { position: 'relative', width: '80px', height: '80px' },
  img: { width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px' },
  imgDel: { position: 'absolute', top: '-6px', right: '-6px', width: '20px', height: '20px', background: '#ff4d4f', color: '#fff', border: 'none', borderRadius: '50%', fontSize: '14px', cursor: 'pointer', lineHeight: '20px', padding: 0, textAlign: 'center' },
  uploadBtn: { width: '80px', height: '80px', border: '1.5px dashed #ccc', borderRadius: '8px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: '#fafafa', gap: '4px' },
  submitBtn: { width: '100%', padding: '14px', background: 'linear-gradient(90deg, #1a4a8a, #2d7dd2)', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '17px', fontWeight: 700, cursor: 'pointer', marginTop: '10px' }
};
